import React, { useState } from "react";
import {
  faBell,
  faBuilding,
  faCoins,
  faFileInvoice,
  faGlobe,
  faPalette,
  faUsers,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { Profile } from "../../../core/models/entities/Profile";
import { Organization } from "../../../core/models/entities/Organization";
import { LogoOptions } from "../../../core/models/entities/LogoOptions";
import { CommissionProfile } from "../../../core/models/entities/Commission";
import { VendorType } from "../../../core/models/entities/enums/OrganizationType";
import { User } from "../../../core/models/entities/User";
import { CreateUserDto } from "../../../core/models/dtos/CreateUserDto";
import { UpdateUserDto } from "../../../core/models/dtos/UpdateUserDto";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { withToasts } from "../../components/Toast/Toast";
import { OrgScreenShell } from "../../components/OrgShell/OrgScreenShell";
import { SectionNavItem } from "../../components/OrgShell/SectionNavItem";
import { BadgeChip } from "../../components/OrgShell/BadgeChip";
import { FieldGrid, FieldGroup } from "../../components/OrgShell/FieldGroup";
import { InputWrapper } from "../../components/Input/InputWrapper";
import { InputLabel } from "../../components/Input/InputLabel";
import { CustomInput } from "../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../components/Input/CustomPhoneInput";
import {
  CommissionEditorV2,
  RateCard,
} from "../../components/commission/commission-editor-v2.component";
import VATField from "../../components/vat/VATField";
import LoadingElementIsolated from "../../components/loading-element/loading-element-isolated";
import { DetailsSection } from "../customer-update/component/sections/details-section.component";
import { ShippingSection } from "../customer-update/component/sections/shipping-section.component";
import {
  ContactFields,
  UsersContactSection,
} from "../customer-update/component/sections/users-contact-section.component";
import { OrganizationUpdateForm } from "../customer-update/models/organization-update.form";
import { DetailsForm } from "../customer-create/components/organization-details-form.component";
import { EmailPreferenceForm } from "./subcomponents/email-preference-editor.component";
import { OrderOptionsForm } from "./subcomponents/order-options-editor.component";
import { WhiteLabelConfigurationsForm } from "./subcomponents/white-label-editor.components";
import { OrderOptionsEditorWrapper } from "./subcomponents/order-options-editor.wrapper";
import { ExcludedDomainsEditorWrapper } from "./subcomponents/excluded-domains-editor.wrapper";
import { InvoiceAddressEditorWrapper } from "./subcomponents/invoice-address-editor.wrapper";
import { InvoiceAddressForm } from "./subcomponents/invoice-address-editor.component";
import { VendorDetailsSection } from "./subcomponents/sections/vendor-details-section.component";
import { BrandingSection } from "./subcomponents/sections/branding-section.component";
import { NotificationsSection } from "./subcomponents/sections/notifications-section.component";

export interface MyProfileViewModel {
  saveProfileChanges: () => void;
  resetProfile: () => void;
  updateLogoHandle: (file: File) => void;
  removeLogoHandle?: () => void;
  cancelLogoRemovalHandle?: () => void;
  updateWhiteLabel: (form: WhiteLabelConfigurationsForm) => void;
  updateOrganizationDetails: (form: DetailsForm) => void;
  updateEmailPreferences: (form: EmailPreferenceForm) => void;
  updateOrderOptions: (form: OrderOptionsForm) => void;
  sections: ProfileSectionConfigurations;
  profile: Profile;
  detailsForm: DetailsForm;
  emailPreferenceForm: EmailPreferenceForm;
  loading: boolean;
  transition: boolean;
  isDirty: boolean;
  logoOptions?: LogoOptions;
  updateCommission?: (commission: CommissionProfile) => void;
  updateVATRate?: (value: number) => void;
  commissionProfile?: CommissionProfile;
  /**
   * Warms the Commission & VAT section's query caches (fail-silent, no-op for
   * workshop users). Fired on nav hover; the viewmodel also runs it idle
   * after first paint. Vendor profile only.
   */
  prefetchCommissionData?: () => void;
  vendorId?: number;
  organization?: Organization;
  isVendor: boolean;
  isAdmin: boolean;
  pendingLogoUploadPreview?: string;
  pendingLogoRemoval?: boolean;
}

export interface ProfileSectionConfigurations {
  details?: SectionConditions;
  whiteLabelling?: SectionConditions;
  emailPreferences?: SectionConditions;
  commissions?: SectionConditions;
  orderOptions?: SectionConditions;
  excludedDomains?: SectionConditions;
}

export interface SectionConditions {
  show: boolean;
  canEdit: boolean;
}

interface SectionDefinition<TId extends string> {
  id: TId;
  label: string;
  icon: IconDefinition;
}

function shippingDiffersFromCompany(form: DetailsForm): boolean {
  const pairs: [string | undefined, string | undefined][] = [
    [form.shippingName, form.name],
    [form.shippingAddress, form.address],
    [form.shippingPostCode, form.postCode],
    [form.shippingCity, form.city],
    [form.shippingCountry, form.country],
    [form.shippingContactPerson, form.contactPerson],
    [form.shippingContactPhone, form.phone],
    [form.shippingContactEmail, form.email],
  ];
  const anyFilled = pairs.some(([shipping]) => !!shipping);
  if (!anyFilled) return false;
  return pairs.some(
    ([shipping, company]) => (shipping ?? "") !== (company ?? "")
  );
}

function mirrorCompanyIntoShipping(form: DetailsForm): DetailsForm {
  return {
    ...form,
    shippingName: form.name,
    shippingAddress: form.address,
    shippingPostCode: form.postCode,
    shippingCity: form.city,
    shippingCountry: form.country,
    shippingContactPerson: form.contactPerson,
    shippingContactPhone: form.phone,
    shippingContactEmail: form.email,
  };
}

/**
 * The invoice address has no persistence of its own (no endpoint stores it) —
 * it mirrors the company details, so the section renders read-only. Editing
 * here used to be silently discarded on save.
 */
function invoiceFormFromDetails(form: DetailsForm): InvoiceAddressForm {
  return {
    invoiceFullName: form.name ?? "",
    invoiceAddress: form.address ?? "",
    invoiceZipCode: form.postCode ?? "",
    invoiceCity: form.city ?? "",
  };
}

function brandingFormOf(profile: Profile): WhiteLabelConfigurationsForm {
  return {
    colorHex: profile.whiteLabelConfigurations?.colorHex ?? "#7BBB58",
    whiteLabelCopyrightName:
      profile.whiteLabelConfigurations?.whiteLabelCopyrightName ?? "",
    whiteLabelContactEmail:
      profile.whiteLabelConfigurations?.whiteLabelContactEmail ?? "",
    whiteLabelContactPhone:
      profile.whiteLabelConfigurations?.whiteLabelContactPhone ?? "",
    whiteLabelVendorName:
      profile.whiteLabelConfigurations?.whiteLabelVendorName ?? "",
    whiteLabelWebsiteLink:
      profile.whiteLabelConfigurations?.whiteLabelWebsiteLink ?? "",
  };
}

// ---------------------------------------------------------------------------
// Vendor profile
// ---------------------------------------------------------------------------

type VendorSectionId =
  | "details"
  | "branding"
  | "commission"
  | "notifications"
  | "users";

const VENDOR_SECTIONS: SectionDefinition<VendorSectionId>[] = [
  { id: "details", label: "Details", icon: faBuilding },
  { id: "branding", label: "Branding", icon: faPalette },
  { id: "commission", label: "Commission & VAT", icon: faCoins },
  { id: "notifications", label: "Notifications", icon: faBell },
  { id: "users", label: "Users & contact", icon: faUsers },
];

export function MyVendorProfile(vm: MyProfileViewModel) {
  const [activeSection, setActiveSection] =
    useState<VendorSectionId>("details");
  // Derived until the user touches the checkbox - the organization (and with
  // it the form baseline) can arrive after the first render.
  const [shippingDiffersOverride, setShippingDiffersOverride] = useState<
    boolean | undefined
  >(undefined);
  const shippingDiffers =
    shippingDiffersOverride ?? shippingDiffersFromCompany(vm.detailsForm);

  const isMainBranch =
    vm.profile.userOrganizationInformations.vendorType ===
    VendorType.MainBranch;
  const canEditBranding = vm.sections.whiteLabelling?.canEdit ?? false;

  function handleShippingDiffersChange(differs: boolean) {
    setShippingDiffersOverride(differs);
    if (!differs) {
      vm.updateOrganizationDetails(mirrorCompanyIntoShipping(vm.detailsForm));
    }
  }

  function handleCancel() {
    setShippingDiffersOverride(undefined);
    vm.resetProfile();
  }

  return (
    <OrgScreenShell
      title={vm.profile.userOrganizationInformations.organizationName}
      subtitle={`My profile${
        vm.organization?.navNumber ? ` · NAV ${vm.organization.navNumber}` : ""
      }`}
      badges={
        <>
          <BadgeChip tone="blue">Vendor</BadgeChip>
          {isMainBranch ? (
            <BadgeChip tone="green">Main branch</BadgeChip>
          ) : (
            <BadgeChip tone="gray">Branch</BadgeChip>
          )}
        </>
      }
      nav={VENDOR_SECTIONS.filter(
        // Workshop-flagged users must not even see the Commission & VAT nav
        // item (its content is already gated via sections.commissions.show).
        (section) =>
          section.id !== "commission" ||
          vm.sections.commissions?.show !== false
      ).map((section) => (
        <SectionNavItem
          key={section.id}
          label={section.label}
          icon={section.icon}
          active={activeSection === section.id}
          onClick={() => setActiveSection(section.id)}
          onPrefetch={
            section.id === "commission" ? vm.prefetchCommissionData : undefined
          }
        />
      ))}
      footer={{
        onCancel: handleCancel,
        onSave: vm.saveProfileChanges,
        isDirty: vm.isDirty,
      }}
      isLoading={vm.transition}
    >
      {activeSection === "details" && (
        <VendorDetailsSection
          form={vm.detailsForm}
          onChange={vm.updateOrganizationDetails}
          navNumber={vm.organization?.navNumber}
          shippingDiffers={shippingDiffers}
          onShippingDiffersChange={handleShippingDiffersChange}
        />
      )}
      {activeSection === "branding" && (
        <BrandingSection
          form={brandingFormOf(vm.profile)}
          onFormChange={vm.updateWhiteLabel}
          onLogoChange={vm.updateLogoHandle}
          onLogoRemove={vm.removeLogoHandle}
          onCancelRemove={vm.cancelLogoRemovalHandle}
          logoUrl={vm.profile.whiteLabelConfigurations?.logoUrl}
          pendingUploadPreview={vm.pendingLogoUploadPreview}
          pendingRemoval={vm.pendingLogoRemoval}
          disabled={!canEditBranding}
          lockedByName={vm.organization?.parent?.name}
        />
      )}
      {activeSection === "commission" && (
        <VendorCommissionSection
          vendorId={vm.vendorId}
          commissionProfile={vm.commissionProfile}
          updateCommission={vm.updateCommission!}
          logoOptions={vm.logoOptions}
          detailsForm={vm.detailsForm}
          updateOrganizationDetails={vm.updateOrganizationDetails}
        />
      )}
      {activeSection === "notifications" && (
        <NotificationsSection
          form={vm.emailPreferenceForm}
          onChange={vm.updateEmailPreferences}
          variant="vendor"
        />
      )}
      {activeSection === "users" && (
        <VendorUsersSection
          organizationId={vm.organization?.id ?? 0}
          contact={{
            contactPerson: vm.detailsForm.contactPerson,
            contactPhone: vm.detailsForm.contactPhone,
            contactEmail: vm.detailsForm.contactEmail,
          }}
          onContactChange={(contact) =>
            vm.updateOrganizationDetails({ ...vm.detailsForm, ...contact })
          }
        />
      )}
    </OrgScreenShell>
  );
}

/**
 * Commission & VAT: the data (GetTemplatesSizes is a slow NAV call) loads only
 * when this section is first opened, because the section component only mounts
 * then. The viewmodel warms these exact caches in the background after first
 * paint (and on nav hover), so the spinner below is a fallback, not the norm.
 */
function VendorCommissionSection({
  vendorId,
  commissionProfile,
  updateCommission,
  logoOptions,
  detailsForm,
  updateOrganizationDetails,
}: {
  vendorId?: number;
  commissionProfile?: CommissionProfile;
  updateCommission: (commission: CommissionProfile) => void;
  logoOptions?: LogoOptions;
  detailsForm: DetailsForm;
  updateOrganizationDetails: (form: DetailsForm) => void;
}) {
  const { logoRepository } = useRepositoriesContext();
  const { data: transferOptions, isSuccess } =
    logoRepository.useTransferOptions(vendorId);

  if (!isSuccess || !logoOptions) {
    return (
      <div className="relative h-24">
        <LoadingElementIsolated transparent />
      </div>
    );
  }

  return (
    <CommissionEditorV2
      userType="vendor"
      commissions={commissionProfile}
      onCommissionUpdate={updateCommission}
      logoOptions={logoOptions}
      options={{
        types: logoOptions.transferLogoTypes,
        transfers: transferOptions?.transferMethods ?? [],
      }}
      extraSummaryCard={
        <RateCard
          title="Default VAT for new customers"
          description="Prefills the VAT rate when you create a new customer."
        >
          <VATField
            value={detailsForm.defaultVatRate ?? 0}
            onChange={(value) =>
              updateOrganizationDetails({
                ...detailsForm,
                defaultVatRate: value,
              })
            }
          />
        </RateCard>
      }
    />
  );
}

/** Users of the vendor's own organization; loads only when the section opens. */
function VendorUsersSection({
  organizationId,
  contact,
  onContactChange,
}: {
  organizationId: number;
  contact: ContactFields;
  onContactChange: (contact: ContactFields) => void;
}) {
  const { userRepository } = useRepositoriesContext();
  const { data: users, isLoading } = userRepository.useUsersOfOrganization(
    organizationId,
    { enabled: organizationId > 0 }
  );
  const [, setActionLoading] = useState(false);

  function handleCreateUser(user: CreateUserDto) {
    async function transaction() {
      user.organizationId = organizationId;
      return await userRepository.createUser(user);
    }
    return withToasts(
      transaction,
      "Creating user",
      "User created",
      "",
      setActionLoading
    );
  }

  function handleDeleteUser(userId: number) {
    return withToasts(
      () => userRepository.deleteUser(userId),
      "Deleting user",
      "User deleted",
      "Failed to delete user",
      setActionLoading
    );
  }

  function handleUpdateUser(user: User, dto: UpdateUserDto) {
    return withToasts(
      () => userRepository.updateUser(user, organizationId, dto),
      "Updating user",
      "User updated",
      "Failed to update user",
      setActionLoading
    );
  }

  function handleToggleUserHidePrices(user: User, hidePrices: boolean) {
    return withToasts(
      () => userRepository.setUserHidePrices(organizationId, user.id, hidePrices),
      "Updating price visibility",
      hidePrices
        ? "Prices are now hidden for this user"
        : "Prices are now visible for this user",
      "Failed to update price visibility",
      setActionLoading
    );
  }

  return (
    <UsersContactSection
      users={users}
      isLoading={isLoading || !users}
      contact={contact}
      onContactChange={onContactChange}
      onCreateUser={handleCreateUser}
      onDeleteUser={handleDeleteUser}
      onUpdateUser={handleUpdateUser}
      onToggleUserHidePrices={handleToggleUserHidePrices}
      hidePricesNote="Hide prices for users that run KitMaker in your workshop. Only you can change this."
    />
  );
}

// ---------------------------------------------------------------------------
// Customer profile
// ---------------------------------------------------------------------------

type CustomerSectionId = "details" | "notifications" | "invoice";

const CUSTOMER_SECTIONS: SectionDefinition<CustomerSectionId>[] = [
  { id: "details", label: "Details", icon: faBuilding },
  { id: "notifications", label: "Notifications", icon: faBell },
  { id: "invoice", label: "Invoice address", icon: faFileInvoice },
];

export function MyCustomerProfile(vm: MyProfileViewModel) {
  const [activeSection, setActiveSection] =
    useState<CustomerSectionId>("details");
  // Derived until the user touches the checkbox - the organization (and with
  // it the form baseline) can arrive after the first render.
  const [shippingDiffersOverride, setShippingDiffersOverride] = useState<
    boolean | undefined
  >(undefined);
  const shippingDiffers =
    shippingDiffersOverride ?? shippingDiffersFromCompany(vm.detailsForm);

  function handleShippingDiffersChange(differs: boolean) {
    setShippingDiffersOverride(differs);
    if (!differs) {
      vm.updateOrganizationDetails(mirrorCompanyIntoShipping(vm.detailsForm));
    }
  }

  function handleCancel() {
    setShippingDiffersOverride(undefined);
    vm.resetProfile();
  }

  function handleContactInput(
    event: React.ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } }
  ) {
    const target = event.target;
    vm.updateOrganizationDetails({
      ...vm.detailsForm,
      [target.name]: target.value,
    });
  }

  return (
    <OrgScreenShell
      title={vm.profile.userOrganizationInformations.organizationName}
      subtitle="My profile"
      badges={<BadgeChip tone="blue">Customer</BadgeChip>}
      nav={CUSTOMER_SECTIONS.map((section) => (
        <SectionNavItem
          key={section.id}
          label={section.label}
          icon={section.icon}
          active={activeSection === section.id}
          onClick={() => setActiveSection(section.id)}
        />
      ))}
      footer={{
        onCancel: handleCancel,
        onSave: vm.saveProfileChanges,
        isDirty: vm.isDirty,
      }}
      isLoading={vm.transition}
    >
      {activeSection === "details" && (
        <div className="space-y-8">
          <DetailsSection
            form={vm.detailsForm as unknown as OrganizationUpdateForm}
            onChange={(patch) =>
              vm.updateOrganizationDetails({
                ...vm.detailsForm,
                ...(patch as Partial<DetailsForm>),
              })
            }
            navNumber={vm.organization?.navNumber}
            isVendor={true}
          />
          <FieldGroup
            title="Contact person"
            description="Used by your vendor for questions about designs and orders."
          >
            <FieldGrid>
              <InputWrapper>
                <InputLabel>Contact person</InputLabel>
                <CustomInput
                  name="contactPerson"
                  value={vm.detailsForm.contactPerson}
                  onChange={handleContactInput}
                />
              </InputWrapper>
              <InputWrapper>
                <InputLabel>Contact email</InputLabel>
                <CustomInput
                  type="email"
                  name="contactEmail"
                  value={vm.detailsForm.contactEmail}
                  onChange={handleContactInput}
                />
              </InputWrapper>
              <InputWrapper>
                <InputLabel>Contact phone</InputLabel>
                <CustomPhoneInput
                  name="contactPhone"
                  value={vm.detailsForm.contactPhone}
                  disabled={false}
                  onChange={handleContactInput}
                />
              </InputWrapper>
            </FieldGrid>
          </FieldGroup>
          <ShippingSection
            form={vm.detailsForm}
            onChange={(patch) =>
              vm.updateOrganizationDetails({ ...vm.detailsForm, ...patch })
            }
            differs={shippingDiffers}
            onDiffersChange={handleShippingDiffersChange}
          />
        </div>
      )}
      {activeSection === "notifications" && (
        <NotificationsSection
          form={vm.emailPreferenceForm}
          onChange={vm.updateEmailPreferences}
          variant="customer"
        />
      )}
      {activeSection === "invoice" && (
        <div className="space-y-3">
          <InvoiceAddressEditorWrapper
            initialForm={invoiceFormFromDetails(vm.detailsForm)}
            onFormChange={() => undefined}
            disabled={true}
            initialState={true}
          />
          <p className="text-xs text-gray-500">
            Your invoice address follows your company details. To change it,
            update the name and address in the Details section.
          </p>
        </div>
      )}
    </OrgScreenShell>
  );
}

// ---------------------------------------------------------------------------
// Admin profile
// ---------------------------------------------------------------------------

type AdminSectionId = "orderDefaults" | "excludedDomains";

const ADMIN_SECTIONS: SectionDefinition<AdminSectionId>[] = [
  { id: "orderDefaults", label: "Order defaults", icon: faCoins },
  { id: "excludedDomains", label: "Excluded domains", icon: faGlobe },
];

export function MyAdminProfile(vm: MyProfileViewModel) {
  const [activeSection, setActiveSection] =
    useState<AdminSectionId>("orderDefaults");

  return (
    <OrgScreenShell
      title={vm.profile.userOrganizationInformations.organizationName}
      subtitle="My profile"
      badges={<BadgeChip tone="blue">Administrator</BadgeChip>}
      nav={ADMIN_SECTIONS.map((section) => (
        <SectionNavItem
          key={section.id}
          label={section.label}
          icon={section.icon}
          active={activeSection === section.id}
          onClick={() => setActiveSection(section.id)}
        />
      ))}
      footer={{
        onCancel: vm.resetProfile,
        onSave: vm.saveProfileChanges,
        isDirty: vm.isDirty,
      }}
      isLoading={vm.transition}
    >
      {activeSection === "orderDefaults" && (
        <OrderOptionsEditorWrapper
          profile={vm.profile}
          onFormChange={vm.updateOrderOptions}
          disabled={!(vm.sections.orderOptions?.canEdit ?? false)}
          initialState={true}
        />
      )}
      {activeSection === "excludedDomains" && (
        <ExcludedDomainsEditorWrapper disabled={false} initialState={true} />
      )}
    </OrgScreenShell>
  );
}
